import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Spinner from "../../shared/components/Spinner";
import { selectLoaderValue } from "../../../redux/Slices/loadingSlice";
import { deleteUserEducation, useAppDispatch } from "../../../redux";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Education {
    id?: string;
    degree: string;
    school: string;
    year: string;
}

interface NewEducationModalProps {
    open: boolean;
    onClose: () => void;
    onSave: any;
    initialData: any;
}
const emptyEntry = (): Education => ({
    degree: "",
    school: "",
    year: "",
});
const NewEducationModal: React.FC<NewEducationModalProps> = ({ open, onSave, onClose, initialData }) => {
    const { t } = useTranslation();
    const [certificates, setCertificates] = useState<Education[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>("");
    const isLoading = useSelector(selectLoaderValue);
    const dispatch = useAppDispatch();
    const handleChange = (index: number, field: keyof Education, value: string) => {
        const updated = [...certificates];
        updated[index][field] = value;
        setCertificates(updated);
        setErrorMessage(""); // clear on input
    };

    const addNewCertification = () => {
        setCertificates([...certificates, { degree: "", school: "", year: "" }]);
        setErrorMessage(""); // Clear error
    };

    const removeCertification = async (index: number) => {
        const certificate = certificates[index];
        if (certificate?.id) {
            await dispatch(deleteUserEducation({ id: certificate.id }));
        }

        const updated = [...certificates];
        updated.splice(index, 1);
        setCertificates(updated);
    };

    const isYearValid = (year: string) => {
        const num = parseInt(year);
        return num >= 1900 && num <= 4500;
    };

    const handleSave = async () => {
        const isValid = certificates.every(cert => cert.degree.trim() && cert.school.trim() && isYearValid(cert.year));

        if (!isValid) {
            setErrorMessage(t("newEducationModal.errors.fillAllFields"));
            return;
        }

        if (certificates.length === 0) {
            setErrorMessage(t("newEducationModal.errors.addAtLeastOne"));
            return;
        }

        onSave(certificates);
        onClose();
    };

    useEffect(() => {
        if (initialData?.length > 0) {
            setCertificates(initialData.map(item => ({ ...item }))); // shallow clone
        } else {
            setCertificates([emptyEntry()]);
        }
    }, [open, initialData]);
    useEffect(() => {
        if (open && certificates.length === 0) {
            setCertificates([{ degree: "", school: "", year: "" }]);
        }
    }, [open]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold">{t("newEducationModal.title")}</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
                        &times;
                    </button>
                </div>

                <button
                    className="bg-blue-600 text-white px-4 py-2 rounded-md mb-4 hover:bg-blue-700"
                    onClick={addNewCertification}>
                    {t("newEducationModal.addNew")}
                </button>

                {certificates.map((cert, index) => (
                    <div key={index} className="border border-gray-300 rounded-md p-4 mb-4 relative">
                         <div className="flex justify-between items-start">
                        <h4 className="font-medium mb-2">{t("newEducationModal.educationNumber")} {index + 1}</h4>
                        <button
                            onClick={() => removeCertification(index)}
                            className="text-gray-400 hover:text-gray-600">
                            <X className="w-4 h-4" />
                        </button>
                        </div>
                        <label className="block text-sm font-medium mb-1">{t("newEducationModal.degree")}</label>
                        <input
                            type="text"
                            value={cert.degree}
                            onChange={e => handleChange(index, "degree", e.target.value)}
                            className="w-full mb-2 p-2 border rounded-md"
                        />

                        <label className="block text-sm font-medium mb-1">{t("newEducationModal.school")}</label>
                        <input
                            type="text"
                            value={cert.school}
                            onChange={e => handleChange(index, "school", e.target.value)}
                            className="w-full mb-2 p-2 border rounded-md"
                        />

                        <label className="block text-sm font-medium mb-1">{t("newEducationModal.year")}</label>
                        <input
                            type="number"
                            value={cert.year}
                            onChange={e => handleChange(index, "year", e.target.value)}
                            className="w-full mb-2 p-2 border rounded-md"
                            min={1900}
                            max={2100}
                        />
                    </div>
                ))}

                {errorMessage && <div className="text-red-600 text-sm mb-4">{errorMessage}</div>}

                {certificates.length > 0 && (
                    <div className="flex justify-end space-x-3 mt-6">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100">
                            {t("newEducationModal.close")}
                        </button>
                        <button
                            onClick={handleSave}
                            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-800 flex items-center gap-2">
                            {isLoading ? (
                                <>
                                    <Spinner size={5} />
                                    {t("newEducationModal.loading")}
                                </>
                            ) : (
                                t("newEducationModal.saveChanges")
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default NewEducationModal;
